import React, { useState } from 'react';
import { useContext } from 'react';
import { CustomTranslationContext } from '../../../../../../context/CustomTranslationContext';
import {
  UpdateCustomTranslationRecordResponse,
  UpdateCustomTranslationRecordVariables,
  CustomTranslationMutations,
} from '../../../../../../graphql/translations/customTranslation.graphql';
import useMutation from '../../../../../../hooks/useMutation';
import { useToast } from '../../../../../../providers/ToastProvider';
import { TranslationUtils } from '../../../../../../types/translation/common';
import { CustomTranslation } from '../../../../../../types/translation/customTranslation';
import { TranslationKey } from '../../../../../../types/translation/TranslationKey';
import { englishTranslations, t, translationsManager } from '../../../../../../types/translation/Translator';
import { CancelButton, SaveButton } from '../../../../../components/Button';
import { ActionModal } from '../../../../../components/Modal';
import { TextInput } from '../../../../../components/TextInput';
import { ToastType } from '../../../../../components/Toast';

interface CustomTranslationRecordModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  translationKey: TranslationKey;
  customTranslation: CustomTranslation;
}

export default function CustomTranslationRecordModal({
  open,
  setOpen,
  translationKey,
  customTranslation,
}: CustomTranslationRecordModalProps) {
  if (!open) return null;

  const { customTranslations, setCustomTranslations } = useContext(CustomTranslationContext);

  const existing = customTranslation.translations[translationKey];
  const [record, setRecord] = useState(existing ? existing.toRecord() : { singular: { lower: '' } });
  const { showToast } = useToast();

  const [update, { loading }] = useMutation<
    UpdateCustomTranslationRecordResponse,
    UpdateCustomTranslationRecordVariables
  >(CustomTranslationMutations.updateRecord, {
    onCompleted: response => {
      showToast({ type: ToastType.success, title: 'Custom translation updated successfully!' });
      const updated = new CustomTranslation(response.updateCustomTranslationRecord);
      customTranslations.set(response.updateCustomTranslationRecord.id, updated);
      setCustomTranslations(new Map(customTranslations));
      translationsManager.setCustomTranslation(updated);
      setOpen(false);
    },
  });

  const englishRecord = englishTranslations[translationKey]?.toRecord();
  if (!englishRecord) return null;

  const handleSave = async () => {
    await update({
      variables: {
        key: translationKey,
        companyId: customTranslation.companyId,
        customTranslationId: customTranslation.id,
        record,
      },
    });
  };

  const hasVariables =
    TranslationUtils.checkVariables(englishRecord.singular.lower, record.singular.lower) &&
    TranslationUtils.checkVariables(englishRecord.singular.upper || '', record.singular.upper || '') &&
    TranslationUtils.checkVariables(englishRecord.plural?.lower || '', record.plural?.lower || '') &&
    TranslationUtils.checkVariables(englishRecord.plural?.upper || '', record.plural?.upper || '');

  const hasRequired =
    !(englishRecord.singular.lower && !record.singular.lower) &&
    !(englishRecord.singular.upper && !record.singular.upper) &&
    !(englishRecord.plural?.lower && !record.plural?.lower) &&
    !(englishRecord.plural?.upper && !record.plural?.upper);

  const canSave = () => {
    if (!record.singular.lower && !record.singular.upper && !record.plural?.lower && !record.plural?.upper) return true;
    return hasVariables && hasRequired;
  };

  const footer = (
    <div className='flex items-center justify-end gap-2'>
      <CancelButton onClick={() => setOpen(false)} disabled={loading} />
      <SaveButton disabled={!canSave()} onClick={handleSave} loading={loading} />
    </div>
  );

  return (
    <ActionModal
      title={t(TranslationKey.customTranslationRecordModalTitle).singular.upper}
      setOpen={setOpen}
      open={open}
      footer={footer}
    >
      <div className='grid gap-4'>
        <div className='grid gap-2'>
          <TextInput
            label={t(TranslationKey.englishSingular).singular.upper}
            disabled={true}
            value={englishRecord.singular.lower}
          />
          <TextInput
            label={t(TranslationKey.customSingular).singular.upper}
            value={record.singular.lower}
            placeholder={t(TranslationKey.customSingularPlaceholder).singular.upper}
            onChange={value => setRecord({ ...record, singular: { lower: value } })}
          />
        </div>
        {englishRecord.plural?.lower ? (
          <div className='grid gap-2'>
            <TextInput
              label={t(TranslationKey.englishPlural).singular.upper}
              disabled={true}
              value={englishRecord.plural?.lower}
            />
            <TextInput
              label={t(TranslationKey.customPlural).singular.upper}
              value={record.plural?.lower}
              placeholder={t(TranslationKey.customPluralPlaceholder).singular.upper}
              onChange={value => setRecord({ ...record, plural: { lower: value } })}
            />
          </div>
        ) : null}
      </div>
    </ActionModal>
  );
}
